import { Component, OnInit, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { Floor } from '../firestore-models/floor';
import { ProjectLanguage } from '../firestore-models/project-language';
import { ApiFloor, ApiSetFloorsRequest } from '../api-client-module';

import { ApiClientService } from '../api-client.service';
import { ConfigurationService } from '../configuration.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';

import { FloorDialogComponent } from './floor-dialog/floor-dialog.component';
import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-floors',
  templateUrl: './floor.component.html',
  styleUrls: ['./floor.component.css'],
  standalone: false,
})
export class FloorComponent implements OnInit {

  //Floorコレクション
  private floorsCollection!: AngularFirestoreCollection<Floor>;
  floorsObservable!: Observable<Floor[]>;
  floorsSubscribe!: Subscription;
  //各フロア情報に紐付くプロジェクトID
  projectId: string;
  //各フロア情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;

  //フロア情報テーブル
  displayedColumns: string[] = ['position', 'edit', 'select', 'floor_id', 'name', 'explain', 'exhibit', 'file', 'date'];
  dataSource = new MatTableDataSource<Floor>();
  selection = new SelectionModel<Floor>(true, []);

  uploadedFilesCollectionName = AppConst.FIRESTORE_UPLOADED_FILES;

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.floorsCollection = this.angularFirestore.collection<Floor>(
      AppConst.FIRESTORE_FLOORS, ref => ref.where('project_id', '==', this.projectId).orderBy('floor_id', 'asc'));
    this.floorsObservable = this.floorsCollection.valueChanges({ idField: 'id' });
    this.floorsSubscribe = this.floorsObservable.subscribe(floors => {
      this.dataSource.data = this.addFilterValue(floors);
    });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
  }

  ngOnDestroy(): void {
    this.floorsSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: Floor): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //編集ダイアログ
  openEditDialog(floor: Floor): void {
    const startDate: any = floor!.event_start_date ? new Date(floor!.event_start_date.seconds * 1000) : null;
    const endDate: any = floor!.event_end_date ? new Date(floor!.event_end_date.seconds * 1000) : null;
    let apiFloor: ApiFloor = {
      floor_id: floor.floor_id,
      floor_explain: floor.floor_explain,
      hall_id: floor.hall_id,
      floor_names: floor.floor_names,
      floor_descs: floor.floor_descs,
      exhibit_ids: floor.exhibit_ids,
      floor_files: floor.floor_files,
      floor_file_expand: floor.floor_file_expand,
      latitude: !!floor.floor_geopoint ? floor.floor_geopoint.latitude : AppConst.LATITUDE_INITIAL_VALUE,
      longitude: !!floor.floor_geopoint ? floor.floor_geopoint.longitude : AppConst.LONGITUDE_INITIAL_VALUE,
      show_marker: floor.show_marker,
      externallink_names: floor.externallink_names,
      externallink_urls: floor.externallink_urls,
      use_externallink: floor.use_externallink,
      event_start_date: startDate,
      event_end_date: endDate,
      project_id: floor.project_id,
      enabled: floor.enabled,
    };
    const dialogRef = this.dialog.open(FloorDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, floor: apiFloor }
    });
  }

  //追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(FloorDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, floor: null }
    });
  }

  //削除
  deleteData(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedFloors: Array<ApiFloor> = new Array<ApiFloor>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedFloor: ApiFloor = {
            floor_id: row.floor_id,
            floor_explain: row.floor_explain,
            hall_id: row.hall_id,
            floor_names: row.floor_names,
            floor_descs: row.floor_descs,
            exhibit_ids: row.exhibit_ids,
            floor_files: row.floor_files,
            floor_file_expand: row.floor_file_expand,
            // latitude: row.floor_geopoint.latitude,
            // longitude: row.floor_geopoint.longitude,
            show_marker: row.show_marker,
            externallink_names: row.externallink_names,
            externallink_urls: row.externallink_urls,
            use_externallink: row.use_externallink,
            project_id: row.project_id,
            enabled: row.enabled,
          };
          selectedFloors.push(selectedFloor);
        }
      }
      if (!!result) {
        let request: ApiSetFloorsRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          floors: selectedFloors
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setFloors(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'));
            } else {
              if (response!.ret === AppConst.API_RET_IN_USE_DATA) {
                this.toastr.error(this.translate.instant('削除できませんでした。施設情報で利用中のデータが含まれている可能性があります。'));
              } else {
                this.toastr.error(this.translate.instant('削除できませんでした。'));
              }
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'floor_id':
          return compare(a.floor_id, b.floor_id, isAsc);
        case 'name':
          const aName = a.floor_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          const bName = b.floor_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          return compare(aName, bName, isAsc);
        case 'explain':
          return compare(a.floor_explain, b.floor_explain, isAsc);
        case 'date':
          return compare(a.updated.toMillis(), b.updated.toMillis(), isAsc);
        default:
          return 0;
      }
    });
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  addFilterValue(docs: Array<any>) :Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      for (const value of doc.floor_names) {
        filterValue += value.name + ' '
      }
      doc['filterValue'] = filterValue;
    }
    return docs;
  }


}
